import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNotification } from '../../hooks/useNotification'
import services from '../../services'
import { FiltroTela, SeletorLoja, faltaNoSeletor, novoSeletorLoja, trocarFiltroDoSeletor } from './configRelatorio'
import './abaDados.scss'

interface CampoDic { nome: string }

/** O que o servidor diz do seletor: de onde vem o que vale hoje e por que ele pode não estar aparecendo. */
interface SeletorTela {
  origem: 'dashboard' | 'html' | 'desligado' | 'nenhuma'
  config?: SeletorLoja | null
  filtrosDaTela: { id: string; coluna: string; rotulo?: string | null }[]
  versaoNumero?: number | null
  versaoPublicada: boolean
  problemas: string[]
}

const RAIOS = [200, 500, 1000, 2000, 5000, 10000, 20000]
const rotuloDoRaio = (m: number) => (m < 1000 ? `${m} m` : `${m / 1000} km`)

/**
 * O seletor de loja: no celular, o portal pede a localização, mostra as lojas mais perto e abre o relatório só com a loja escolhida.
 * Fica guardado no dashboard, e não no HTML de cada versão: uma versão nova (do Claude, sem o seletor no JSON) não o desliga.
 * A única exigência da versão é declarar em filtros_tela o filtro que recebe a loja.
 */
export default function AbaSeletorLoja({ dashboardId, dicionario }: {
  dashboardId: number
  dicionario: { colunas: CampoDic[]; medidas: CampoDic[] }
}) {
  const { notify } = useNotification()
  const [dados, setDados] = useState<SeletorTela | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [rascunho, setRascunho] = useState<SeletorLoja | undefined>(undefined)
  const [sujo, setSujo] = useState(false)
  const [trabalhando, setTrabalhando] = useState(false)
  const [novoFiltro, setNovoFiltro] = useState('')

  const carregar = useCallback(async () => {
    const r: any = await services.api.relatorioHtmlSeletorLoja(dashboardId)
    if (r.fail) { setErro(r.error); return }
    const d: SeletorTela = r.data
    setDados(d)
    setRascunho(d.origem === 'dashboard' || d.origem === 'html' ? d.config ?? undefined : undefined)
    setSujo(false)
    setErro(null)
  }, [dashboardId])

  useEffect(() => { carregar() }, [carregar])

  const filtros: FiltroTela[] = useMemo(
    () => (dados?.filtrosDaTela ?? []).map(f => ({ id: f.id, coluna: f.coluna, rotulo: f.rotulo ?? undefined })), [dados])
  const { colunas, medidas } = dicionario

  const mudar = (s: SeletorLoja | undefined) => { setRascunho(s); setSujo(true) }
  const trocar = (campo: Partial<SeletorLoja>) => rascunho && mudar({ ...rascunho, ...campo })
  const trocarColuna = (chave: keyof SeletorLoja['colunas'], valor: string) =>
    rascunho && mudar({ ...rascunho, colunas: { ...rascunho.colunas, [chave]: valor || undefined } as SeletorLoja['colunas'] })

  const salvar = async () => {
    if (!rascunho) return
    setTrabalhando(true)
    try {
      const r: any = await services.api.relatorioHtmlSalvarSeletorLoja(dashboardId, rascunho)
      if (r.fail) { notify(r.error, { variant: 'error' }); return }
      notify('Seletor de loja salvo. Vale para todas as versões, inclusive as novas.', { variant: 'success' })
      await carregar()
    } finally {
      setTrabalhando(false)
    }
  }

  const desligar = async () => {
    setTrabalhando(true)
    try {
      const r: any = await services.api.relatorioHtmlDesligarSeletorLoja(dashboardId)
      if (r.fail) { notify(r.error, { variant: 'error' }); return }
      notify('Seletor de loja desligado.', { variant: 'success' })
      await carregar()
    } finally {
      setTrabalhando(false)
    }
  }

  if (erro) return <div className="aba-dados"><div className="rh-alerta">{erro}</div></div>
  if (!dados) return <div className="aba-dados"><p className="ad-nota">Carregando…</p></div>

  const falta = rascunho ? faltaNoSeletor(rascunho) : []
  const filtroAtual = rascunho ? filtros.find(f => f.id === rascunho.filtro) : undefined

  const seletorDeColuna = (chave: keyof SeletorLoja['colunas'], rotulo: string, obrigatoria: boolean) => {
    const atual = rascunho?.colunas[chave] ?? ''
    return (
      <label key={chave} className="ad-campo">
        <span className="ad-rot">{rotulo}{obrigatoria ? '' : ' (opcional)'}</span>
        <select data-campo={`seletor_loja.colunas.${chave}`} value={atual} onChange={e => trocarColuna(chave, e.target.value)}>
          <option value="">{obrigatoria ? 'Escolha a coluna' : 'Não mostrar'}</option>
          {atual && !colunas.some(c => c.nome === atual) && <option value={atual}>{atual} (fora do dicionário)</option>}
          {colunas.map(c => <option key={c.nome} value={c.nome}>{c.nome}</option>)}
        </select>
      </label>
    )
  }

  const situacao = {
    dashboard: dados.problemas.length === 0
      ? <span className="ng-pill ok">Ligado · vale para todas as versões</span>
      : <span className="ng-pill alerta">Ligado, mas não está aparecendo</span>,
    html: <span className="ng-pill alerta">Ligado só na versão {dados.versaoNumero}</span>,
    desligado: <span className="ng-pill inativo">Desligado</span>,
    nenhuma: <span className="ng-pill">Não configurado</span>
  }[dados.origem]

  return (
    <div className="aba-dados">
      <p className="ad-explica">
        No celular, o portal pede a localização, mostra no mapa e na lista as lojas mais perto e abre o relatório só com a loja escolhida.
        A loja entra no filtro da tela que você escolher abaixo. <b>O seletor fica guardado no dashboard: publicar uma versão nova não o desliga.</b>{' '}
        Ele aparece no relatório publicado, não na prévia.
      </p>

      <section className="ad-cartao" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h2 className="ad-titulo">Seletor de loja</h2>
          {situacao}
        </div>

        {dados.origem === 'html' && (
          <p className="ad-nota ad-alerta">
            Hoje este seletor vem de dentro do HTML da versão {dados.versaoNumero}. Uma versão nova sem ele no JSON deixaria de ter seletor.
            Clique em <b>Salvar seletor</b> para guardá-lo no dashboard e ele passar a valer em todas as versões.
          </p>
        )}
        {dados.problemas.length > 0 && (
          <div className="rh-alerta erro">
            <b>O seletor não está aparecendo para quem abre o relatório:</b>
            <ul>{dados.problemas.map((p, i) => <li key={i}>{p}</li>)}</ul>
            <span>Ele volta a aparecer quando a versão publicada declarar em <code>filtros_tela</code> o filtro escolhido abaixo.</span>
          </div>
        )}

        {!rascunho && filtros.length === 0 && (
          <p className="ad-nota">
            {dados.versaoNumero == null
              ? 'Crie uma versão do relatório antes de ligar: o seletor entrega a loja a um dos filtros da tela dela.'
              : 'Antes de ligar, a versão precisa ter um filtro da tela com a coluna que identifica a loja (aba Dados, em "Filtros da tela", ou filtros_tela no HTML).'}
          </p>
        )}

        {!rascunho && filtros.length > 0 && (
          <>
            <label className="ad-campo">
              <span className="ad-rot">Filtro da tela que recebe a loja</span>
              <select data-campo="seletor_loja.filtro" value={(filtros.find(f => f.id === novoFiltro) ?? filtros[0]).id} onChange={e => setNovoFiltro(e.target.value)}>
                {filtros.map(f => <option key={f.id} value={f.id}>{f.rotulo || f.id} ({f.coluna})</option>)}
              </select>
            </label>
            <div>
              <button type="button" className="ng-btn prim"
                      onClick={() => mudar(novoSeletorLoja(filtros.find(f => f.id === novoFiltro) ?? filtros[0], colunas))}>Ligar o seletor de loja</button>
            </div>
          </>
        )}

        {rascunho && (
          <fieldset disabled={trabalhando} className="ad-campos">
            <label className="ad-campo">
              <span className="ad-rot">Filtro da tela que recebe a loja</span>
              <select data-campo="seletor_loja.filtro" value={rascunho.filtro}
                      onChange={e => { const f = filtros.find(x => x.id === e.target.value); if (f) mudar(trocarFiltroDoSeletor(rascunho, f)) }}>
                {!filtroAtual && <option value={rascunho.filtro}>{rascunho.filtro} (não existe nos filtros da tela)</option>}
                {filtros.map(f => <option key={f.id} value={f.id}>{f.rotulo || f.id} ({f.coluna})</option>)}
              </select>
            </label>
            <p className="ad-nota">A coluna do código da loja é a do filtro: <code>{rascunho.colunas.codigo}</code>. Para trocar o código, escolha outro filtro.</p>

            {seletorDeColuna('nome', 'Nome da loja', true)}
            {seletorDeColuna('lat', 'Latitude', true)}
            {seletorDeColuna('lng', 'Longitude', true)}
            {seletorDeColuna('endereco', 'Endereço', false)}
            {seletorDeColuna('cidade', 'Cidade', false)}
            {seletorDeColuna('uf', 'UF', false)}
            {seletorDeColuna('bandeira', 'Bandeira', false)}
            {falta.length > 0 && <p className="ad-nota ad-alerta">Falta escolher {falta.join(', ')}. Se a coluna não aparece na lista, libere na aba Dicionário.</p>}

            <label className="ad-campo">
              <span className="ad-rot">Medida de referência (opcional)</span>
              <select data-campo="seletor_loja.medida" value={rascunho.medida ?? ''} onChange={e => trocar({ medida: e.target.value || undefined })}>
                <option value="">Nenhuma: todas as lojas da tabela</option>
                {rascunho.medida && !medidas.some(m => m.nome === rascunho.medida) && <option value={rascunho.medida}>{rascunho.medida} (fora do dicionário)</option>}
                {medidas.map(m => <option key={m.nome} value={m.nome}>{m.nome}</option>)}
              </select>
            </label>
            <p className="ad-nota">
              Só entram na lista as lojas em que esta medida tem valor, no que a pessoa pode ver. Lojas sem dado não aparecem, então ninguém
              abre um relatório vazio. Use uma medida que conte o que a loja tem no período, como as visitas previstas.
              Com regra de acesso por pessoa, escolher uma medida também impede que a lista traga lojas que a pessoa não veria nos números.
            </p>

            <label className="ad-campo">
              <span className="ad-rot">Raio inicial</span>
              <select data-campo="seletor_loja.raioInicialM" value={rascunho.raioInicialM ?? 2000} onChange={e => trocar({ raioInicialM: Number(e.target.value) })}>
                {(RAIOS.includes(rascunho.raioInicialM ?? 2000) ? RAIOS : [...RAIOS, rascunho.raioInicialM as number].sort((a, b) => a - b))
                  .map(r => <option key={r} value={r}>{rotuloDoRaio(r)}</option>)}
              </select>
            </label>

            <label className="ad-campo ad-marca">
              <input type="checkbox" checked={!!rascunho.obrigatorio} onChange={e => trocar({ obrigatorio: e.target.checked || undefined })} />
              <span>Obrigatório: sem o botão "Ver todas as lojas", só abre quem escolhe uma loja</span>
            </label>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button type="button" className="ng-btn prim" onClick={salvar}
                      disabled={trabalhando || falta.length > 0 || !filtroAtual || (!sujo && dados.origem === 'dashboard')}>Salvar seletor</button>
              {sujo && <button type="button" className="ng-btn" onClick={carregar} disabled={trabalhando}>Desfazer</button>}
              {(dados.origem === 'dashboard' || dados.origem === 'html') && (
                <button type="button" className="ng-btn perigo" onClick={desligar} disabled={trabalhando}>Desligar o seletor</button>
              )}
            </div>
          </fieldset>
        )}

        {!rascunho && dados.origem === 'desligado' && <p className="ad-nota">Desligado de propósito: nenhuma versão mostra o seletor, mesmo que o HTML tenha um. Para voltar, ligue acima.</p>}
      </section>
    </div>
  )
}
